<template>
  <div>
    <!-- 顶部Banner -->
    <div class="top-banner">
      <img src="@/assets/topbanner.jpg" alt="华洲电力" class="banner-img" />
    </div>
    
    <!-- 荣誉资质主体内容 -->
    <div class="honors-container">
      <div class="honors-content">
        <!-- 标题区域 -->
        <div class="title-section">
          <h1>荣誉资质</h1>
          <div class="title-line"></div>
        </div>
        
        <!-- 标签页切换 -->
        <div class="tabs-section">
          <div class="tabs">
            <div 
              class="tab-item" 
              :class="{ active: activeTab === 'honors' }"
              @click="changeTab('honors')"
            >
              企业荣誉
            </div>
            <div 
              class="tab-item" 
              :class="{ active: activeTab === 'qualifications' }"
              @click="changeTab('qualifications')"
            >
              企业资质
            </div>
          </div>
        </div>
        
        <!-- 证书内容展示区域 -->
        <div class="certificates-section">
          <div v-if="activeTab === 'honors'" class="certificates-grid">
            <div 
              v-for="(cert, index) in displayedCertificates" 
              :key="index" 
              class="certificate-item"
              @click="showCertificate(cert)"
            >
              <div class="certificate-image">
                <img :src="cert.image" :alt="cert.name" />
                <div class="zoom-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M21 21L16.65 16.65" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <circle cx="11" cy="11" r="8" stroke="white" stroke-width="2"/>
                  </svg>
                </div>
              </div>
              <div class="certificate-name">{{ cert.name }}</div>
            </div>
          </div>
          
          <div v-else-if="activeTab === 'qualifications'" class="certificates-grid">
            <div 
              v-for="(cert, index) in displayedCertificates" 
              :key="index" 
              class="certificate-item"
              @click="showCertificate(cert)"
            >
              <div class="certificate-image">
                <img :src="cert.image" :alt="cert.name" />
                <div class="zoom-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M21 21L16.65 16.65" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <circle cx="11" cy="11" r="8" stroke="white" stroke-width="2"/>
                  </svg>
                </div>
              </div>
              <div class="certificate-name">{{ cert.name }}</div>
            </div>
          </div>
        </div>
        
        <!-- 分页控件 -->
        <div class="pagination-section">
          <div class="pagination">
            <span v-if="currentPage > 1" @click="changePage(currentPage - 1)" class="page-btn">上一页</span>
            <span 
              v-for="page in totalPages" 
              :key="page" 
              :class="['page-number', { active: page === currentPage }]"
              @click="changePage(page)"
            >
              {{ page }}
            </span>
            <span v-if="currentPage < totalPages" @click="changePage(currentPage + 1)" class="page-btn">下一页</span>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 证书大图预览模态框 -->
    <div v-if="selectedCertificate" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <button class="close-btn" @click="closeModal">×</button>
        <div class="modal-image-container">
          <img :src="selectedCertificate.image" :alt="selectedCertificate.name" class="modal-image" />
        </div>
        <div class="modal-caption">{{ selectedCertificate.name }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useHead } from '@vueuse/head'

// SEO Meta标签配置
useHead({
  title: '荣誉资质 - 华洲电力建设有限公司',
  meta: [
    {
      name: 'description',
      content: '华洲电力建设有限公司荣誉资质展示，包括企业荣誉、资质证书等，彰显公司实力和行业认可。'
    },
    {
      name: 'keywords',
      content: '华洲电力,荣誉资质,企业荣誉,资质证书,守合同重信用'
    },
    {
      name: 'author',
      content: '华洲电力建设有限公司'
    },
    {
      property: 'og:title',
      content: '荣誉资质 - 华洲电力建设有限公司'
    },
    {
      property: 'og:description',
      content: '华洲电力建设有限公司荣誉资质展示，包括企业荣誉、资质证书等，彰显公司实力和行业认可。'
    },
    {
      property: 'og:type',
      content: 'website'
    }
  ]
})

// 状态管理
const activeTab = ref('honors') // 默认选中企业荣誉标签
const currentPage = ref(1) // 当前页码
const itemsPerPage = ref(9) // 每页显示的证书数量
const selectedCertificate = ref(null) // 当前选中的证书，用于显示大图

// 企业荣誉数据 - 使用实际图片文件
const honorsList = [
  { 
    id: 1, 
    name: '先进单位', 
    image: require('@/assets/honor/先进单位.jpg') 
  },
  { 
    id: 2, 
    name: '2020湘潭市守合同重信用证书', 
    image: require('@/assets/honor/2020湘潭市守合同重信用证书.jpg') 
  },
  { 
    id: 3, 
    name: '2021湖南省守合同重信用', 
    image: require('@/assets/honor/2021湖南省守合同重信用.jpg') 
  },
  { 
    id: 4, 
    name: '2021湘潭市守合同重信用', 
    image: require('@/assets/honor/2021湘潭市守合同重信用.jpg') 
  },
  { 
    id: 5, 
    name: '2022年度先进会员单位牌匾', 
    image: require('@/assets/honor/2022年度先进会员单位牌匾.jpg') 
  },
  { 
    id: 6, 
    name: '2022年度安全工作优秀核心外包队伍', 
    image: require('@/assets/honor/2022年度安全工作优秀核心外包队伍.jpg') 
  },
  { 
    id: 7, 
    name: '2022年湖南省守合同重信用', 
    image: require('@/assets/honor/2022年湖南省守合同重信用.jpg') 
  },
  { 
    id: 8, 
    name: '2022年湘潭市守合同重信用证书', 
    image: require('@/assets/honor/2022年湘潭市守合同重信用证书.jpg') 
  },
  { 
    id: 9, 
    name: '2022年纳税增幅突出贡献奖', 
    image: require('@/assets/honor/2022年纳税增幅突出贡献奖.png') 
  },
  { 
    id: 10, 
    name: '2023年度先进会员单位', 
    image: require('@/assets/honor/2023年度先进会员单位.jpg') 
  },
  { 
    id: 11, 
    name: '2023年度湘乡市建筑行业对外开拓先进单位', 
    image: require('@/assets/honor/2023年度湘乡市建筑行业对外开拓先进单位.jpg') 
  },
  { 
    id: 12, 
    name: '2023年湖南省守合同重信用证书', 
    image: require('@/assets/honor/2023年湖南省守合同重信用证书.jpg') 
  },
  { 
    id: 13, 
    name: '2023湘潭市守合同重信用证书', 
    image: require('@/assets/honor/2023湘潭市守合同重信用证书.jpg') 
  },
  { 
    id: 14, 
    name: '2024湖南省守合同重信用企业证书', 
    image: require('@/assets/honor/2024湖南省守合同重信用企业证书.png') 
  },
  { 
    id: 15, 
    name: '2024湘潭市守合同重信用证书', 
    image: require('@/assets/honor/2024湘潭市守合同重信用证书.jpg') 
  },
  { 
    id: 16, 
    name: '中建五局2022年良好履约分包商', 
    image: require('@/assets/honor/中建五局2022年良好履约分包商.jpg') 
  },
  { 
    id: 17, 
    name: '中建五局2024年度优秀履约分包方', 
    image: require('@/assets/honor/中建五局2024年度优秀履约分包方.jpg') 
  },
  { 
    id: 18, 
    name: '湘乡市建筑行业对外开拓先进单位牌匾', 
    image: require('@/assets/honor/湘乡市建筑行业对外开拓先进单位牌匾.jpg') 
  },
  { 
    id: 19, 
    name: '湘潭市住房和城乡建设局AA级信用企业', 
    image: require('@/assets/honor/湘潭市住房和城乡建设局AA级信用企业.jpg') 
  }
]

// 企业资质数据 - 使用state目录下的实际资质证书图片
const qualificationsList = [
  { 
    id: 7, 
    name: '营业执照', 
    image: require('@/assets/state/营业执照副本.jpg') 
  },
  { 
    id: 9, 
    name: '企业资质证书', 
    image: require('@/assets/state/资质.jpg') 
  },
  { 
    id: 2, 
    name: '劳务和模板脚手架证书', 
    image: require('@/assets/state/劳务和模板脚手架副本.jpg') 
  },
  { 
    id: 3, 
    name: '安全生产许可证', 
    image: require('@/assets/state/安全生产许可证.jpg') 
  },
  { 
    id: 4, 
    name: '承装修试二级资质', 
    image: require('@/assets/state/承装修试二级.jpg') 
  },
  { 
    id: 1, 
    name: '3A信用等级证书', 
    image: require('@/assets/state/2025年3A信用等级证书新.jpg') 
  },
  { 
    id: 8, 
    name: '质量管理体系认证证书', 
    image: require('@/assets/state/质量管理体系认证证书.jpg') 
  },
  { 
    id: 5, 
    name: '环境管理体系认证证书', 
    image: require('@/assets/state/环境管理体系认证证书.jpg') 
  },
  { 
    id: 6, 
    name: '职业健康安全管理体系认证证书', 
    image: require('@/assets/state/职业健康安全管理体系认证证书.jpg') 
  }
]

// 获取当前活动的列表
const currentList = computed(() => {
  return activeTab.value === 'honors' ? honorsList : qualificationsList
})

// 计算当前显示的证书列表
const displayedCertificates = computed(() => {
  const startIndex = (currentPage.value - 1) * itemsPerPage.value
  const endIndex = startIndex + itemsPerPage.value
  return currentList.value.slice(startIndex, endIndex)
})

// 计算总页数
const totalPages = computed(() => {
  return Math.ceil(currentList.value.length / itemsPerPage.value)
})

// 切换标签
const changeTab = (tab) => {
  activeTab.value = tab
  currentPage.value = 1 // 切换标签时重置页码
}

// 切换页码
const changePage = (page) => {
  currentPage.value = page
}

// 显示证书大图
const showCertificate = (cert) => {
  selectedCertificate.value = cert
  // 禁止背景滚动
  document.body.style.overflow = 'hidden'
}

// 关闭模态框
const closeModal = () => {
  selectedCertificate.value = null
  // 恢复背景滚动
  document.body.style.overflow = ''
}
</script>

<style scoped>
/* 荣誉资质页面的样式 */
.top-banner {
  width: 100%;
  height: 200px;
  overflow: hidden;
  position: relative;
}

.banner-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.honors-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}

.title-section {
  text-align: center;
  margin-bottom: 40px;
}

.title-section h1 {
  font-size: 28px;
  color: #333;
  margin-bottom: 15px;
}

.title-line {
  width: 80px;
  height: 3px;
  background-color: #3f5ff0;
  margin: 0 auto;
}

.tabs-section {
  margin-bottom: 30px;
  display: flex;
  justify-content: center;
}

.tabs {
  display: flex;
  background-color: #f0f0f0;
  border-radius: 20px;
  padding: 4px;
}

.tab-item {
  padding: 8px 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 20px;
  font-size: 16px;
  color: #666;
}

.tab-item.active {
  background-color: #3f5ff0;
  color: white;
}

.tab-item:hover:not(.active) {
  background-color: #a9b7f4;
  color: #3f5ff0;
}

.certificates-section {
  margin-bottom: 40px;
}

.certificates-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 25px;
}

.certificate-item {
  text-align: center;
  background-color: #f9f9f9;
  transition: transform 0.3s ease;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  cursor: pointer;
}

.certificate-item:hover {
  transform: translateY(-5px);
}

.certificate-image {
  padding: 15px;
  margin-bottom: 5px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 4/3;
  position: relative;
}

.zoom-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.certificate-item:hover .zoom-icon {
  opacity: 1;
}

.certificate-image img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.certificate-item:hover .certificate-image img {
  transform: scale(1.05);
}

.certificate-name {
  font-size: 16px;
  color: #333;
  margin-top: 5px;
  padding: 0 10px 15px 10px;
}

.pagination-section {
  display: flex;
  justify-content: center;
  margin-top: 30px;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 10px;
}

.page-btn {
  padding: 8px 16px;
  background-color: #f5f5f5;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #666;
}

.page-btn:hover {
  background-color: #e0e0e0;
  color: #333;
}

.page-number {
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #666;
}

.page-number.active {
  background-color: #3f5ff0;
  color: white;
}

.page-number:hover:not(.active) {
  background-color: #f0f0f0;
}

/* 响应式设计 */
@media (max-width: 992px) {
  .certificates-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .honors-container {
    padding: 30px 15px;
  }
  
  .title-section h1 {
    font-size: 24px;
  }
  
  .certificates-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .certificate-image {
    padding: 10px;
  }
  
  .tab-item {
    padding: 8px 20px;
    font-size: 14px;
  }
  
  /* 移动端模态框适配 */
  .modal-image {
    max-width: 90%;
    max-height: 70vh;
  }
  
  .close-btn {
    font-size: 24px;
    padding: 10px;
  }
}

/* 模态框样式 */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.8);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: fadeIn 0.3s ease;
  }
  
  .modal-content {
    position: relative;
    background-color: white;
    border-radius: 8px;
    padding: 20px;
    max-width: 90%;
    max-height: 90vh;
    overflow: auto;
    animation: scaleIn 0.3s ease;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  
  .modal-image-container {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 20px 0;
  }
  
  .modal-image {
    max-width: 100%;
    max-height: 70vh;
    object-fit: contain;
  }
  
  .modal-caption {
    font-size: 16px;
    color: #333;
    text-align: center;
    margin-top: 15px;
    margin-bottom: 10px;
    width: 100%;
  }
  
  .close-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    background: none;
    border: none;
    font-size: 30px;
    color: #666;
    cursor: pointer;
    padding: 5px;
    transition: color 0.3s ease;
    z-index: 1001;
  }

.close-btn:hover {
  color: #333;
}

/* 动画效果 */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes scaleIn {
  from { transform: scale(0.9); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
</style>